import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  Spinner,
  Table,
  type BadgeVariant,
  type TableColumn,
} from "../../ds";
import { api, errorText, type Backup, type DeviceDetail } from "../../lib/api";
import { fmtBytes, fmtDateTime } from "../../lib/format";
import { BackupRestoreModal } from "./BackupRestoreModal";

// Backup-Katalog dieses Routers (`GET /api/backups?device=<id>`). Die Archive
// liegen verschlüsselt auf dem Server; der Download ist ein normaler Link — die
// Bytes kommen entschlüsselt vom Server, der Browser packt nichts aus.

const STATUS: Record<Backup["status"], { variant: BadgeVariant; label: string }> = {
  pending: { variant: "warn", label: "wird geladen" },
  ok: { variant: "success", label: "vollständig" },
  error: { variant: "danger", label: "fehlerhaft" },
};

interface RowActions {
  onRestore: (backup: Backup) => void;
  onDelete: (backup: Backup) => void;
  /** Eine Aktion läuft — solange kein zweiter Knopf. */
  busy: boolean;
  /** Nur Einspielen braucht den Router; Download und Löschen laufen am Server. */
  deviceOnline: boolean;
}

function backupColumns({
  onRestore,
  onDelete,
  busy,
  deviceOnline,
}: RowActions): TableColumn<Backup>[] {
  return [
    { key: "createdAt", header: "Zeitpunkt", cell: (b) => fmtDateTime(b.createdAt) },
    {
      key: "firmware",
      header: "Firmware",
      hideOnMobile: true,
      cell: (b) => b.firmwareVersion ?? "—",
    },
    { key: "model", header: "Modell", hideOnMobile: true, cell: (b) => b.model ?? "—" },
    {
      key: "size",
      header: "Größe",
      num: true,
      hideOnMobile: true,
      cell: (b) => fmtBytes(b.sizeBytes),
    },
    {
      key: "sha",
      header: "sha256",
      mono: true,
      hideOnMobile: true,
      cell: (b) => `${b.sha256.slice(0, 12)}…`,
    },
    { key: "trigger", header: "Auslöser", hideOnMobile: true, cell: (b) => b.trigger ?? "—" },
    {
      key: "status",
      header: "Status",
      cell: (b) => <Badge variant={STATUS[b.status].variant}>{STATUS[b.status].label}</Badge>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "actions",
      cell: (b) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            iconName="download"
            disabled={b.status !== "ok"}
            title="Entschlüsselt vom Server herunterladen"
            onClick={() => window.location.assign(api.backupDownloadUrl(b.id))}
          >
            Download
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconName="upload"
            disabled={busy || !deviceOnline || b.status !== "ok"}
            title={deviceOnline ? undefined : "Router ist offline"}
            onClick={() => onRestore(b)}
          >
            Einspielen
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconName="trash"
            disabled={busy}
            onClick={() => onDelete(b)}
          >
            Löschen
          </Button>
        </div>
      ),
    },
  ];
}

function DeleteBackupModal({
  backup,
  busy,
  onClose,
  onConfirm,
}: {
  backup: Backup | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (!backup) return null;
  return (
    <Modal
      open
      onClose={onClose}
      title="Backup löschen?"
      size="sm"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="danger" iconName="trash" loading={busy} onClick={onConfirm}>
            Löschen
          </Button>
        </>
      }
    >
      <p className="text-sm text-content-2">
        Das Archiv vom {fmtDateTime(backup.createdAt)} wird samt Eintrag entfernt. Es gibt keine
        zweite Kopie.
      </p>
    </Modal>
  );
}

export function BackupTable({ device }: { device: DeviceDetail }) {
  const [rows, setRows] = useState<Backup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<Backup | null>(null);
  const [deleting, setDeleting] = useState<Backup | null>(null);
  const [busy, setBusy] = useState(false);

  // Reiner Server-Katalog — kostet kein Router-Volumen.
  const load = useCallback(() => {
    api
      .listBackups(device.id)
      .then((b) => {
        setRows(b);
        setError(null);
      })
      .catch((e: unknown) => {
        setRows([]);
        setError(errorText(e));
      });
  }, [device.id]);

  useEffect(load, [load]);

  async function act(run: () => Promise<unknown>, okText: string) {
    setBusy(true);
    setError(null);
    try {
      await run();
      setNote(okText);
      load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
      setRestoring(null);
      setDeleting(null);
    }
  }

  const columns = backupColumns({
    onRestore: setRestoring,
    onDelete: setDeleting,
    busy,
    deviceOnline: device.online,
  });

  return (
    <>
      <Card
        title="Backups auf dem Server"
        subtitle="Verschlüsselt abgelegt, entschlüsselt nur beim Download oder beim Einspielen."
        actions={
          <Button size="sm" variant="outline" iconName="refresh" onClick={load}>
            Neu laden
          </Button>
        }
        bodyPadding={false}
      >
        <div className="flex flex-col gap-3 p-4">
          {error && <Alert variant="danger">{error}</Alert>}
          {note && (
            <Alert variant="success" dismissible onDismiss={() => setNote(null)}>
              {note}
            </Alert>
          )}
          {rows === null ? (
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              iconName="box"
              title="Noch kein Backup"
              description="„Backup erstellen“ legt ein Konfigurationsarchiv auf dem Server ab. Automatisch passiert nichts."
            />
          ) : (
            <div className="-mx-4 -mb-4">
              <Table columns={columns} rows={rows} rowKey={(b) => b.id} />
            </div>
          )}
        </div>
      </Card>

      <BackupRestoreModal
        key={restoring?.id ?? "keins"}
        backup={restoring}
        device={device}
        busy={busy}
        onClose={() => setRestoring(null)}
        onConfirm={() => {
          if (restoring) {
            void act(
              () => api.restoreBackup(restoring.id, device.id),
              "Einspielen ausgelöst — der Router lädt das Archiv und startet danach neu.",
            );
          }
        }}
      />

      <DeleteBackupModal
        backup={deleting}
        busy={busy}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) void act(() => api.deleteBackup(deleting.id), "Backup gelöscht.");
        }}
      />
    </>
  );
}
